import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { ROUTES } from '@/config/routeConfig';

interface ProtectedRouteProps {
  children: ReactNode;
  requireAuth?: boolean;
  fallbackPath?: string;
}

const ProtectedRoute = ({
  children,
  requireAuth = true,
  fallbackPath = ROUTES.LOGIN
}: ProtectedRouteProps) => {
  const { currentUser, isInitialized } = useUser();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Small delay to ensure user state is properly loaded
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Show loading spinner while checking authentication or if context is not initialized
  if (isLoading || !isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p>
            {!isInitialized ? 'Initializing...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  // If authentication is not required, render children
  if (!requireAuth) {
    return <>{children}</>;
  }

  // If no user is logged in, redirect to login
  if (!currentUser) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // All checks passed, render children
  return <>{children}</>;
};

export default ProtectedRoute; 